/**
 * The right-click menu on a list row.
 *
 * Every desk in this CRM is a list you work down, and the actions on a row were
 * only reachable through a 44px three-dot button at its end - or, on the mail
 * and call desks, not from the list at all: you had to open a conversation to
 * archive it. A right-click is the gesture people already try there, and until
 * now it produced the browser's own menu, which offers "View page source" to
 * somebody trying to label a lead.
 *
 * One menu for the whole list rather than one per row. A list can hold two
 * hundred rows and each of them mounting a portal, a placement effect and four
 * window listeners is a scroll that stutters. The row travels in the handle's
 * state instead, so exactly one panel exists however long the list is.
 *
 * It deliberately does NOT replace the three-dot buttons. A right-click is not
 * discoverable, it is not available on a touch screen in every browser, and it
 * is not reachable by a keyboard on macOS at all. Every entry that appears here
 * also appears somewhere you can point at.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@wa/ui";
import { useClosePops, usePopOpen } from "./Pop";
import { focusableStops, moveWithinGroup, stepFocus } from "./popFocus";
import { placeAtPoint, type Placed } from "./contextPlace";

/** An open menu: which row it is for, where the pointer was, what to give focus
 *  back to when it closes. */
interface OpenAt<T> {
  target: T;
  x: number;
  y: number;
  anchor: HTMLElement | null;
}

/** Everything a row has to carry to raise this menu, by either gesture. */
export interface RowMenuProps {
  onContextMenu: (e: React.MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
  onClickCapture: (e: React.MouseEvent) => void;
}

export interface ContextMenuHandle<T> {
  at: OpenAt<T> | null;
  /** Bind to a row's `onContextMenu`. Mouse only. */
  open: (e: React.MouseEvent, target: T) => void;
  /**
   * Spread onto a row to get both gestures at once.
   *
   * `onContextMenu` is the whole story on a desktop and none of it on a phone:
   * a touch screen has no right button, and Safari raises no `contextmenu` for
   * a long press on a `<button>`. Everything in this menu was reachable some
   * other way when it was built - that was the rule - but on the mail and call
   * desks "some other way" means opening the row first, so filing three
   * conversations became three round trips through the reading pane.
   */
  rowProps: (target: T) => RowMenuProps;
  close: () => void;
}

/** How long a finger has to stay put before it counts as a press rather than
 *  the start of a scroll. The platform figure on both iOS and Android. */
const LONG_PRESS_MS = 500;
/** And how far it may drift while it waits. Beyond this it was a scroll. */
const LONG_PRESS_SLOP = 10;

/**
 * Keyboard-raised menus (the Menu key, Shift+F10) carry no pointer position.
 * Chrome answers with the element's own corner, Firefox with the caret's, and
 * some builds with nothing at all - so a non-positive pair is read as "there was
 * no pointer" and the row's box is used instead.
 */
const NO_POINTER = (e: React.MouseEvent) => e.clientX <= 0 && e.clientY <= 0;

export function useContextMenu<T>(): ContextMenuHandle<T> {
  const [at, setAt] = useState<OpenAt<T> | null>(null);
  const closePops = useClosePops();
  const popOpen = usePopOpen();

  const close = useCallback(() => setAt(null), []);

  const open = useCallback(
    (e: React.MouseEvent, target: T) => {
      // A text field keeps the browser's menu: cut, paste and the spelling
      // suggestions are genuinely better than anything this app can offer, and
      // no row action is worth taking them away. Everywhere else on the row we
      // win - the entries here are the reason the gesture is worth learning.
      if ((e.target as HTMLElement | null)?.closest("input, textarea, [contenteditable='true']")) return;

      e.preventDefault();
      e.stopPropagation();
      closePops();

      const el = e.currentTarget as HTMLElement;
      const box = el.getBoundingClientRect();
      const keyed = NO_POINTER(e);
      setAt({
        target,
        x: keyed ? box.left + 24 : e.clientX,
        y: keyed ? box.bottom : e.clientY,
        anchor: el,
      });
    },
    [closePops],
  );

  /**
   * The same menu, raised by a long press.
   *
   * Deliberately NOT `e.preventDefault()` on touchstart: that would cancel the
   * scroll before we know whether one was starting, and the list would feel
   * stuck for half a second on every drag. The press is armed instead and
   * disarmed by the first move past the slop, which is what makes the gesture
   * cost nothing when it is not the gesture the operator meant.
   *
   * The row's own tap is not suppressed either. Once the panel is up it has the
   * pointer, and the `touchend` that follows lands on the panel's scrim rather
   * than on the row, so a press does not also open the conversation.
   */
  const press = useRef<{ timer: number; x: number; y: number } | null>(null);
  /** A press just raised the menu, so the click the browser sends after the
   *  finger lifts is not a tap on the row - it is the tail of the gesture. */
  const raised = useRef(false);

  const cancelPress = useCallback(() => {
    if (!press.current) return;
    window.clearTimeout(press.current.timer);
    press.current = null;
  }, []);

  const rowProps = useCallback(
    (target: T): RowMenuProps => ({
      onContextMenu: (e) => open(e, target),
      onTouchStart: (e) => {
        const touch = e.touches[0];
        cancelPress();
        if (!touch || e.touches.length > 1) return;
        if ((e.target as HTMLElement | null)?.closest("input, textarea, [contenteditable='true']")) return;
        const el = e.currentTarget as HTMLElement;
        const { clientX: x, clientY: y } = touch;
        press.current = {
          x,
          y,
          timer: window.setTimeout(() => {
            press.current = null;
            raised.current = true;
            closePops();
            setAt({ target, x, y, anchor: el });
          }, LONG_PRESS_MS),
        };
      },
      onTouchMove: (e) => {
        const armed = press.current;
        const touch = e.touches[0];
        if (!armed || !touch) return;
        // Past the slop it was a scroll, not a press. Checked rather than
        // assumed: a finger resting on a list drifts a pixel or two, and
        // cancelling on any movement at all would make the gesture a lottery.
        if (Math.abs(touch.clientX - armed.x) > LONG_PRESS_SLOP || Math.abs(touch.clientY - armed.y) > LONG_PRESS_SLOP) {
          cancelPress();
        }
      },
      onTouchEnd: cancelPress,
      onTouchCancel: cancelPress,
      // Capture, so the row's own handler never runs: without this a long
      // press opened the menu AND the conversation under it, and the menu was
      // then acting on a row the operator had already navigated away from.
      onClickCapture: (e) => {
        if (!raised.current) return;
        raised.current = false;
        e.preventDefault();
        e.stopPropagation();
      },
    }),
    [cancelPress, closePops, open],
  );

  // A dropdown opening takes the floor. `Pop` stops its own mousedown before it
  // reaches the document, so the outside-click listener below never hears it.
  useEffect(() => {
    if (popOpen) setAt(null);
  }, [popOpen]);

  // Nothing may outlive the row that armed it: a list reloading under a finger
  // would otherwise raise a menu for a conversation that is no longer there.
  useEffect(() => cancelPress, [cancelPress]);

  return { at, open, rowProps, close };
}

export function ContextMenu<T>({
  menu,
  label,
  children,
}: {
  menu: ContextMenuHandle<T>;
  /** Announced on the panel. The list says what its rows are; the panel cannot
   *  work it out from the entries. */
  label: string;
  children: (target: T, close: () => void) => ReactNode;
}) {
  const { at, close } = menu;
  const panelRef = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<Placed | null>(null);
  /** One entry into the panel per opening, however often it is measured. */
  const entered = useRef(false);

  // Layout, not effect: the panel is measured before the browser paints, or it
  // shows up in the window's top-left corner for a frame on the way to the
  // pointer. It is `visibility: hidden` until `place` lands, for the same frame.
  useLayoutEffect(() => {
    if (!at) {
      setPlace(null);
      entered.current = false;
      return;
    }
    const position = () => {
      const panel = panelRef.current;
      if (!panel) return;
      // `offsetWidth/Height`, NOT `getBoundingClientRect`. This runs on the
      // panel's first committed frame, where the entry animation is still at
      // its opening keyframe - `scale(0.98)` and an 8px shift - and a rect
      // carries that transform. Measured that way a 420px menu reports 412,
      // the bottom clamp is computed against the wrong height, and a menu
      // opened low on the screen hangs a few pixels off the window's edge.
      // The offset properties are the untransformed layout box.
      setPlace(
        placeAtPoint(
          { x: at.x, y: at.y },
          { width: panel.offsetWidth, height: panel.offsetHeight },
          { width: document.documentElement.clientWidth, height: document.documentElement.clientHeight },
        ),
      );
    };
    position();
    // A menu whose rows arrive late (the label registry is fetched once per
    // desk) grows after it opens; without this it keeps the height it had
    // while empty and hangs off the bottom of the window.
    const observer = new ResizeObserver(position);
    if (panelRef.current) observer.observe(panelRef.current);
    return () => observer.disconnect();
  }, [at]);

  /** Focus lands on the first row once the panel has a position - nothing inside
   *  a `visibility: hidden` subtree can take it, so this cannot run earlier. */
  useLayoutEffect(() => {
    if (!at || !place || entered.current) return;
    const panel = panelRef.current;
    if (!panel) return;
    entered.current = true;
    (focusableStops(panel)[0] ?? panel).focus({ preventScroll: true });
  }, [at, place]);

  /**
   * Closing.
   *
   * A context menu closes on a scroll rather than following it, which is the
   * opposite of what `Pop` does - and right, because a `Pop` belongs to a button
   * that scrolls with the page while this one belongs to a point the pointer was
   * at, and that point stops meaning anything the moment the rows move under it.
   */
  useEffect(() => {
    if (!at) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Stopped at the document, the same way `PopHost` stops it: every other
      // Escape owner in this app listens on `window`, one hop later, so without
      // this a menu opened inside a dialog would take the dialog with it.
      e.preventDefault();
      e.stopPropagation();
      close();
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("blur", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("blur", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [at, close]);

  /** The panel has unmounted and taken focus with it. Back to the row, which is
   *  where the operator was - `<body>` would lose their place in a long list. */
  const anchor = at?.anchor ?? null;
  const lastAnchor = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (anchor) {
      lastAnchor.current = anchor;
      return;
    }
    const back = lastAnchor.current;
    lastAnchor.current = null;
    if (!back || !back.isConnected) return;
    if (document.activeElement === null || document.activeElement === document.body) back.focus({ preventScroll: true });
  }, [anchor]);

  if (!at) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        close();
        return;
      case "ArrowDown":
        e.preventDefault();
        stepFocus(panelRef.current, 1);
        return;
      case "ArrowUp":
        e.preventDefault();
        stepFocus(panelRef.current, -1);
        return;
      case "Home":
        e.preventDefault();
        stepFocus(panelRef.current, 1, -1);
        return;
      case "End":
        e.preventDefault();
        stepFocus(panelRef.current, -1, -1);
        return;
      case "ArrowRight":
        if (moveWithinGroup(document.activeElement, 1)) e.preventDefault();
        return;
      case "ArrowLeft":
        if (moveWithinGroup(document.activeElement, -1)) e.preventDefault();
        return;
      case "Tab":
        // Not trapped: this is a menu, not a dialog. Closing hands focus back to
        // the row and the browser tabs onwards from there.
        close();
        return;
      default:
    }
  };

  return createPortal(
    <div
      ref={panelRef}
      role="menu"
      aria-label={label}
      // Focusable but not tabbable: somewhere for focus to sit on a panel whose
      // rows have not arrived yet, so Escape and the arrows still work.
      tabIndex={-1}
      className={cn("crm-menu", place?.up && "crm-menu-up")}
      style={{
        top: place?.top ?? 0,
        left: place?.left ?? 0,
        visibility: place ? "visible" : "hidden",
      }}
      onKeyDown={onKeyDown}
      // The portal keeps the React tree even after leaving the DOM tree, so
      // these run before the document listener above and a click on a row does
      // not close the menu out from under its own handler.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {children(at.target, close)}
    </div>,
    document.body,
  );
}
